import React, { useState, useEffect } from 'react';
import { Modal, Table, Button } from 'antd';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const formatToIST = (timestamp) => {
	if (!timestamp || timestamp === '-' || timestamp === '') return '-';

	try {
		const date = new Date(timestamp);
		// Check if the date is valid
		if (Number.isNaN(date.getTime())) {
			return '-';
		}

		const istOffset = 5.5 * 60 * 60 * 1000;
		const istDate = new Date(date.getTime() + istOffset);

		return istDate.toLocaleString('en-IN', {
			timeZone: 'Asia/Kolkata',
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			hour12: false,
		});
	} catch (error) {
		console.error('Error formatting timestamp:', error);
		return '-'; // Return '-' if parsing fails
	}
};

// Table columns configuration
const getColumns = () => [
	{
		title: 'Serial No',
		dataIndex: 'serial_no',
		key: 'serial_no',
		width: 120,
		align: 'left',
		render: (text, record, index) => `${String(index + 1).padStart(2, '0')}`,
	},
	{
		title: 'Loaded at',
		dataIndex: 'loaded_at',
		key: 'loaded_at',
		align: 'center',
		render: (text, record) => formatToIST(record.loaded_at),
	},
	{
		title: 'Unloaded at',
		dataIndex: 'unloaded_at',
		key: 'unloaded_at',
		align: 'center',
		render: (text, record) => formatToIST(record.unloaded_at),
	},
];

const StyledModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 4px;
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
		padding: 0;
	}
	.ant-modal-body {
		padding: 24px;
	}
	.ant-modal-header {
		border-bottom: none;
		padding: 24px 24px 0 24px;
	}
	.ant-modal-title {
		font-family: 'Inter', sans-serif;
		font-weight: 600;
		font-size: 18px;
		color: #000;
		text-align: center;
	}
	.ant-modal-footer {
		display: none;
	}

	/* Ensure scrollbars are visible on all platforms only when needed */
	.ant-table-body {
		overflow-y: auto !important;
		scrollbar-width: thin;
		scrollbar-color: #d9d9d9 #f5f5f5;
	}

	/* Only show scrollbars when content overflows */
	.ant-table-body::-webkit-scrollbar {
		width: 8px;
		height: 8px;
	}

	.ant-table-body::-webkit-scrollbar-track {
		background: #f5f5f5;
		border-radius: 4px;
	}

	.ant-table-body::-webkit-scrollbar-thumb {
		background: #d9d9d9;
		border-radius: 4px;
	}

	.ant-table-body::-webkit-scrollbar-thumb:hover {
		background: #bfbfbf;
	}

	/* Hide scrollbars when not needed */
	.ant-table-body::-webkit-scrollbar-thumb:vertical {
		min-height: 0;
	}

	/* Custom class for when scrollbars should be hidden */
	.ant-table-body.no-scroll::-webkit-scrollbar {
		display: none;
	}

	.ant-table-body.no-scroll {
		scrollbar-width: none;
		-ms-overflow-style: none;
	}

	/* Hide scrollbars for table when not needed */
	.no-scrollbar-table .ant-table-body::-webkit-scrollbar {
		display: none;
	}

	.no-scrollbar-table .ant-table-body {
		scrollbar-width: none;
		-ms-overflow-style: none;
	}
`;

const ModalContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
`;

const CloseButton = styled(Button)`
	background-color: #143e60;
	border-color: #143e60;
	color: white;
	border-radius: 4px;
	height: 40px;
	font-weight: 500;

	&:hover {
		background-color: #0f2d4a;
		border-color: #0f2d4a;
		color: white;
	}
`;

function LoadingHistoryModal({ visible, onClose, loadStatusHistory = [] }) {
	const [historyData, setHistoryData] = useState([]);
	const [showScrollbar, setShowScrollbar] = useState(false);

	// Get columns configuration
	const columns = getColumns();

	// Update history data when modal opens or loadStatusHistory changes
	console.log('loadStatusHistory', loadStatusHistory);
	useEffect(() => {
		if (visible && loadStatusHistory) {
			setHistoryData(loadStatusHistory);

			// Check if scrollbar is needed after a short delay to allow DOM to render
			setTimeout(() => {
				const tableBody = document.querySelector('.ant-table-body');
				if (tableBody) {
					const needsScrollbar = tableBody.scrollHeight > tableBody.clientHeight;
					setShowScrollbar(needsScrollbar);
					console.log(
						'Scrollbar needed:',
						needsScrollbar,
						'scrollHeight:',
						tableBody.scrollHeight,
						'clientHeight:',
						tableBody.clientHeight
					);
				}
			}, 100);
		}
	}, [visible, loadStatusHistory]);

	const handleClose = () => {
		setHistoryData([]);
		onClose();
	};

	return (
		<StyledModal
			title="Loading History"
			open={visible}
			onCancel={handleClose}
			footer={null}
			centered
			width={600}
			closable
		>
			<ModalContent>
				{historyData.length > 0 ? (
					<Table
						columns={columns}
						dataSource={historyData}
						pagination={false}
						rowKey={(record, index) => index}
						scroll={{ y: 300 }}
						className={!showScrollbar ? 'no-scrollbar-table' : ''}
					/>
				) : (
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							padding: '40px 20px',
							minHeight: '200px',
						}}
					>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								marginBottom: '16px',
							}}
						>
							<svg
								width="174"
								height="143"
								viewBox="0 0 174 143"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M87.1504 142.705C122.371 142.705 150.922 137.353 150.922 130.751C150.922 124.149 122.371 118.797 87.1504 118.797C51.9303 118.797 23.3788 124.149 23.3788 130.751C23.3788 137.353 51.9303 142.705 87.1504 142.705Z"
									fill="#F5F5F7"
									fillOpacity="0.8"
								/>
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M138.165 95.6323L115.661 67.8467C114.581 66.5388 113.003 65.747 111.341 65.747H62.9547C61.2936 65.747 59.7152 66.5388 58.6354 67.8467L36.1318 95.6323V110.148H138.166V95.6323H138.165Z"
									fill="#AEB8C2"
								/>
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M55.1995 29.8854H119.099C120.097 29.8854 121.054 30.283 121.76 30.9909C122.465 31.6988 122.862 32.6589 122.862 33.6599V121.744C122.862 122.745 122.465 123.705 121.76 124.413C121.054 125.121 120.097 125.518 119.099 125.518H55.1995C54.2016 125.518 53.2446 125.121 52.539 124.413C51.8334 123.705 51.437 122.745 51.437 121.744V33.6599C51.437 32.6589 51.8334 31.6988 52.539 30.9909C53.2446 30.283 54.2016 29.8854 55.1995 29.8854V29.8854Z"
									fill="#F5F5F7"
								/>
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M63.5219 39.2783H110.776C111.275 39.2783 111.754 39.4772 112.106 39.8311C112.459 40.185 112.657 40.6651 112.657 41.1656V64.7162C112.657 65.2168 112.459 65.6968 112.106 66.0507C111.754 66.4047 111.275 66.6035 110.776 66.6035H63.5219C63.023 66.6035 62.5445 66.4047 62.1917 66.0507C61.8389 65.6968 61.6407 65.2168 61.6407 64.7162V41.1656C61.6407 40.6651 61.8389 40.185 62.1917 39.8311C62.5445 39.4772 63.023 39.2783 63.5219 39.2783ZM63.7684 76.8487H110.53C111.094 76.8487 111.635 77.0736 112.034 77.4739C112.433 77.8742 112.657 78.4171 112.657 78.9832C112.657 79.5493 112.433 80.0923 112.034 80.4926C111.635 80.8929 111.094 81.1178 110.53 81.1178H63.7684C63.2041 81.1178 62.6629 80.8929 62.2639 80.4926C61.8649 80.0923 61.6407 79.5493 61.6407 78.9832C61.6407 78.4171 61.8649 77.8742 62.2639 77.4739C62.6629 77.0736 63.2041 76.8487 63.7684 76.8487ZM63.7684 87.9488H110.53C111.094 87.9488 111.635 88.1738 112.035 88.5742C112.434 88.9745 112.658 89.5176 112.658 90.0838C112.658 90.6501 112.434 91.1931 112.035 91.5935C111.635 91.9939 111.094 92.2188 110.53 92.2188H63.7684C63.204 92.2188 62.6626 91.9939 62.2635 91.5935C61.8644 91.1931 61.6402 90.6501 61.6402 90.0838C61.6402 89.5176 61.8644 88.9745 62.2635 88.5742C62.6626 88.1738 63.204 87.9488 63.7684 87.9488ZM137.958 128.999C137.229 131.897 134.669 134.057 131.623 134.057H42.6748C39.6291 134.057 37.0687 131.896 36.3407 128.999C36.2018 128.447 36.1317 127.88 36.1318 127.31V95.6348H60.8872C63.6216 95.6348 65.8255 97.9449 65.8255 100.749V100.787C65.8255 103.591 68.0548 105.855 70.7892 105.855H103.509C106.243 105.855 108.473 103.57 108.473 100.765V100.754C108.473 97.9496 110.676 95.6339 113.411 95.6339H138.166V127.311C138.166 127.893 138.094 128.459 137.958 128.999Z"
									fill="#DCE0E6"
								/>
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M141.069 31.4159L134.645 33.9166C134.48 33.9809 134.3 33.9973 134.126 33.9637C133.952 33.9301 133.791 33.848 133.662 33.7267C133.533 33.6055 133.44 33.45 133.395 33.2783C133.349 33.1065 133.353 32.9255 133.406 32.7559L135.228 26.8987C132.793 24.1206 131.363 20.7329 131.363 17.0772C131.363 7.64543 140.88 0 152.621 0C164.359 0 173.877 7.64543 173.877 17.0772C173.877 26.5089 164.36 34.1544 152.62 34.1544C148.361 34.1544 144.395 33.1484 141.069 31.4159Z"
									fill="#DCE0E6"
								/>
								<path
									d="M160.997 20.1623C162.477 20.1623 163.677 18.973 163.677 17.5059C163.677 16.0388 162.477 14.8495 160.997 14.8495C159.517 14.8495 158.317 16.0388 158.317 17.5059C158.317 18.973 159.517 20.1623 160.997 20.1623Z"
									fill="white"
								/>
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M146.928 19.8295H141.569L144.295 15.1811L146.928 19.8295ZM150.278 15.1811H154.967V19.8295H150.278V15.1811Z"
									fill="white"
								/>
							</svg>
						</div>
						<div
							style={{
								color: '#000',
								fontSize: '16px',
								fontWeight: '500',
								textAlign: 'center',
							}}
						>
							No Updates Found
						</div>
					</div>
				)}

				<div style={{ textAlign: 'center', marginTop: 20 }}>
					<CloseButton onClick={handleClose}>Close</CloseButton>
				</div>
			</ModalContent>
		</StyledModal>
	);
}

LoadingHistoryModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	loadStatusHistory: PropTypes.array,
};

LoadingHistoryModal.defaultProps = {
	loadStatusHistory: [],
};

export default LoadingHistoryModal;
